import { Box, styled } from '@material-ui/core';
import {
  SBase,
  SButton,
  SFormikTextfield,
  SText
} from '@setvi/shared/components';
import { useDialog } from '@setvi/shared/providers';
import { AIIcon } from '@setvi/shared/components/sicons';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useCustomErrors } from 'Hooks/useCustomErrors';
import { Product } from '@setvi/shared/interfaces';
import { formId } from '@setvi/shared/components/intelligence/product-match/components/form/text';
import { ProductMatchSubmitButton } from '@setvi/shared/components/intelligence/product-match/components/form/components/buttons';
import { RecommendationAnalysis } from '../analysis';

interface RecommendationRequestProps {
  products: Product[];
}

export const RecommendationPurposeId = 'recommendation-request-dialog';

const TitleIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.palette.primary.main,
  borderRadius: '50%',
  padding: 8
}));

const StyledForm = styled(Form)({
  display: 'flex',
  flexDirection: 'column',
  gap: 32
});

const maxLength = 2048;
const name = 'purpose';

const purposeSchema = Yup.object({
  [name]: Yup.string().max(maxLength, `Limit is ${maxLength} characters`)
});

const initialValues = {
  [name]: ''
};

export const RecommendationRequest = ({
  products
}: RecommendationRequestProps) => {
  const { openDialog, closeDialog } = useDialog();

  const { customErrors, customValidate } = useCustomErrors({
    schemas: [
      {
        name,
        schema: purposeSchema
      }
    ],
    values: {
      value: initialValues
    }
  });

  const handleSubmit = (pupose: string) => {
    closeDialog(RecommendationPurposeId);
    openDialog(<RecommendationAnalysis purpose={pupose} products={products} />);
  };

  return (
    <SBase
      width={600}
      header={{
        title: (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gridGap: 8
            }}>
            <TitleIcon>
              <AIIcon fill="#fff" />
            </TitleIcon>
            <SText title="Recommendation Analysis" size="xl" weight="bold" />
          </Box>
        ),
        divider: false,
        onClose: () => closeDialog(RecommendationPurposeId)
      }}>
      <Formik
        onSubmit={values => handleSubmit(values[name])}
        validateOnChange={false}
        initialValues={initialValues}>
        {({ values }) => (
          <StyledForm id={formId}>
            <SText noLineClamp weight="semibold" size="sm">
              Tell us what it’s for—purpose, use case, or business type. The
              more details you give, the better the AI can recommend the right
              product.
            </SText>
            <SFormikTextfield
              name={name}
              placeholder="EX: For cleaning table or For hospital surgery room"
              onChange={(val: string) => {
                customValidate({
                  schema: purposeSchema,
                  value: val,
                  name
                });
              }}
              multiline
              minRows={4}
              maxRows={4}
              InputProps={{
                style: {
                  borderRadius: 12
                }
              }}
              customErrors={customErrors}
            />

            <Box
              width="100%"
              display="flex"
              justifyContent="flex-end"
              gridGap={16}
              mt={3}>
              <SButton
                variant="text"
                onClick={() => closeDialog(RecommendationPurposeId)}
                color="default">
                Cancel
              </SButton>
              <ProductMatchSubmitButton
                label="Let's Go"
                // @ts-ignore
                disabled={!values[name].length || customErrors[name]}
              />
            </Box>
          </StyledForm>
        )}
      </Formik>
    </SBase>
  );
};
